import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { getContactMessages, markContactRead } from '../lib/api'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Modal } from '../components/ui/Modal'
import { Spinner } from '../components/ui/Spinner'
import { EmptyState } from '../components/ui/EmptyState'
import { PageHeader } from '../components/ui/PageHeader'

export default function Contact() {
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState(null)
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['contact', page],
    queryFn: () => getContactMessages({ page, pageSize: 20 }),
  })

  const readMut = useMutation({
    mutationFn: markContactRead,
    onSuccess: () => qc.invalidateQueries(['contact']),
    onError: (e) => toast.error(e.message),
  })

  function openMessage(msg) {
    setSelected(msg)
    if (!msg.isRead) readMut.mutate(msg.id)
  }

  return (
    <div>
      <PageHeader title="Messages" description="Contact form submissions from users" />

      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
      ) : !data?.items?.length ? (
        <EmptyState icon="ti-mail" title="No messages yet" description="Contact form submissions will appear here" />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">From</th>
                <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Subject</th>
                <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Date</th>
                <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Status</th>
                <th className="px-5 py-3.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data?.items?.map(msg => (
                <tr key={msg.id} className={`transition-colors hover:bg-gray-50/50 ${!msg.isRead ? 'bg-indigo-50/30' : ''}`}>
                  <td className="px-5 py-4">
                    <div>
                      <p className={`font-medium ${!msg.isRead ? 'text-gray-900' : 'text-gray-700'}`}>{msg.name}</p>
                      <p className="text-xs text-gray-400">{msg.email}</p>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-gray-700 max-w-xs truncate">{msg.subject}</td>
                  <td className="px-5 py-4 text-gray-400 text-xs whitespace-nowrap">
                    {new Date(msg.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-5 py-4">
                    <Badge color={msg.isRead ? 'gray' : 'indigo'}>{msg.isRead ? 'Read' : 'New'}</Badge>
                  </td>
                  <td className="px-5 py-4">
                    <Button variant="ghost" size="xs" className="text-gray-400 hover:text-indigo-600" onClick={() => openMessage(msg)}>
                      <i className="ti ti-eye" /> View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {data?.totalPages > 1 && (
            <div className="px-5 py-3.5 border-t border-gray-100 flex items-center justify-between">
              <p className="text-xs text-gray-500">Page {data.page} of {data.totalPages} · {data.total} messages</p>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                  <i className="ti ti-chevron-left" /> Previous
                </Button>
                <Button variant="secondary" size="sm" disabled={page === data.totalPages} onClick={() => setPage(p => p + 1)}>
                  Next <i className="ti ti-chevron-right" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal open={Boolean(selected)} onClose={() => setSelected(null)} title={selected?.subject || 'Message'}>
        {selected && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-xl p-4 text-sm">
              <div><p className="text-xs text-gray-400 mb-0.5">From</p><p className="font-medium text-gray-900">{selected.name}</p></div>
              <div><p className="text-xs text-gray-400 mb-0.5">Email</p><p className="font-medium text-gray-900">{selected.email}</p></div>
              <div><p className="text-xs text-gray-400 mb-0.5">Subject</p><p className="font-medium text-gray-900">{selected.subject}</p></div>
              <div><p className="text-xs text-gray-400 mb-0.5">Date</p><p className="font-medium text-gray-900">{new Date(selected.createdAt).toLocaleString('en-US')}</p></div>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-1.5">Message</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{selected.message}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
